import React, { useState } from 'react';
import { MutationStatus } from '@tanstack/react-query';
import { Color } from '@mui/material';

import { FieldValues, useFormState } from 'react-hook-form';
import type { Control } from 'react-hook-form';

import { SchemaObjectDescription } from 'yup';

import _map from 'lodash/map';
import _keys from 'lodash/keys';

import FormStepperBanner, { IStep } from './FormStepperBanner';
import FormStepperBody from './FormStepperBody';

interface FormStepperProps<TFieldValues extends FieldValues> {
  children: ({ activeStep }: { activeStep: number }) => React.ReactNode;
  color: Color;
  reportedBy: string;
  steps: string[];
  schema: SchemaObjectDescription;
  title: string;
  submitError?: boolean;
  control: Control<TFieldValues>;
  onSave?: () => void;
  isDirty?: boolean;
  saveStatus?: MutationStatus;
  lastSaved?: string | null;
  enableAutoSave?: boolean;
  onClose?: () => void;
  hideSave?: boolean;
  submitText?: string;
  reportedByVerbage?: string;
}

function FormStepper<TFieldValues extends FieldValues>(props: FormStepperProps<TFieldValues>): JSX.Element {
  const {
    steps,
    schema,
    control,
    ...rest
  } = props;
  const [stepValues, setStepValues] = useState<IStep[]>([]);

  const [activeStep, setActiveStep] = useState<number>(0);
  const { errors, isValid } = useFormState({ control });

  React.useEffect(() => {
    const result = steps.map((value, index) => ({
      id: index,
      name: value,
      hasError: false,
    }));
    setStepValues(result);
  }, [steps]);

  React.useEffect(() => {
    let erroredSteps: number[] = [];

    if (!isValid) {
      // TODO: Get TS to play nicely with this,
      //  the meta object is available, but TS claims otherwise.
      // @ts-ignore
      erroredSteps = _map(_keys(errors), (key: string) => schema.fields[key].meta.step);
    }

    setStepValues((prevState) => prevState.map((step) => ({
      ...step,
      hasError: erroredSteps.includes(step.id),
    })));
  }, [errors, isValid, schema]);

  return (
    <React.Fragment>
      <FormStepperBanner onStepChange={setActiveStep} activeStep={activeStep} stepValues={stepValues} {...rest} />
      <FormStepperBody onStepChange={setActiveStep} activeStep={activeStep} lastStep={stepValues.length - 1} {...rest} />
    </React.Fragment>
  );
}

export default FormStepper;
